import { useEffect, useState } from 'react';
import { FotoboxRecord } from '../types';

const API_URL = import.meta.env.VITE_API_URL || '';

interface Subscription {
  id: string;
  email: string;
  box_ids: string[];
  label: string | null;
  created_at: number;
}

interface Props {
  onClose: () => void;
  token: string;
  boxes: FotoboxRecord[];
}

export default function SubscriptionsPanel({ onClose, token, boxes }: Props) {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);

  // Formular-State
  const [email, setEmail] = useState('');
  const [label, setLabel] = useState('');
  const [selectedBoxIds, setSelectedBoxIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState(false);

  useEffect(() => {
    loadSubscriptions();
  }, []);

  async function loadSubscriptions() {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setSubscriptions(await res.json() as Subscription[]);
      }
    } finally {
      setLoading(false);
    }
  }

  function toggleBox(id: string) {
    setSelectedBoxIds((prev) =>
      prev.includes(id) ? prev.filter((b) => b !== id) : [...prev, id]
    );
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setFormError('');
    if (!email.includes('@')) { setFormError('Bitte eine gültige E-Mail-Adresse eingeben.'); return; }
    if (selectedBoxIds.length === 0) { setFormError('Bitte mindestens eine Fotobox auswählen.'); return; }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/subscriptions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: email.trim(), box_ids: selectedBoxIds, label: label.trim() || undefined }),
      });
      if (!res.ok) {
        const data = await res.json() as { error?: string };
        setFormError(data.error ?? 'Fehler beim Speichern.');
        return;
      }
      setEmail('');
      setLabel('');
      setSelectedBoxIds([]);
      setFormSuccess(true);
      setTimeout(() => setFormSuccess(false), 2500);
      await loadSubscriptions();
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    await fetch(`${API_URL}/api/subscriptions/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    setSubscriptions((prev) => prev.filter((s) => s.id !== id));
  }

  const sortedBoxes = [...boxes].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      {/* Backdrop – kein backdrop-filter, da Chrome Desktop sonst Scroll-Jank produziert */}
      <div className="fixed inset-0 z-30 bg-[rgba(26,37,53,0.45)]" onClick={onClose} />

      {/* Panel */}
      <div className="fixed right-0 top-0 bottom-0 z-40 w-full max-w-sm bg-white border-l border-surface-border shadow-xl flex flex-col animate-slide-in-right">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-border">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-brand-green" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
            </svg>
            <h2 className="font-raleway font-thin text-sm uppercase tracking-[0.15em] text-ink-primary">
              Benachrichtigungen
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg bg-surface-subtle border border-surface-border flex items-center justify-center text-ink-muted hover:text-ink-primary transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">

          {/* ── Neue Benachrichtigung ── */}
          <section className="space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-ink-muted border-b border-surface-border pb-2">
              Neue Benachrichtigung einrichten
            </h3>
            <p className="text-xs text-ink-muted leading-relaxed">
              Wähle Fotoboxen aus und gib deine E-Mail-Adresse ein. Du erhältst dann
              Statusmeldungen für genau diese Boxen – inklusive Abmelde-Link in jeder Mail.
            </p>

            <form onSubmit={handleCreate} className="space-y-4">
              {/* E-Mail */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-ink-secondary">E-Mail-Adresse</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="max@example.com"
                  className="w-full bg-surface-subtle border border-surface-border rounded-lg px-3 py-1.5 font-mono text-sm text-ink-primary focus:outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors placeholder:text-ink-faint"
                />
              </div>

              {/* Bezeichnung (optional) */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-ink-secondary">
                  Bezeichnung <span className="text-ink-faint font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="z.B. Max – Hochzeit Müller"
                  className="w-full bg-surface-subtle border border-surface-border rounded-lg px-3 py-1.5 text-sm text-ink-primary focus:outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors placeholder:text-ink-faint"
                />
              </div>

              {/* Box-Auswahl */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-ink-secondary">Fotoboxen</label>
                <div className="border border-surface-border rounded-lg">
                  {sortedBoxes.length === 0 && (
                    <p className="px-3 py-2 text-xs text-ink-faint">Keine Fotoboxen vorhanden.</p>
                  )}
                  {sortedBoxes.map((box, i) => {
                    const checked = selectedBoxIds.includes(box.id);
                    return (
                      <label
                        key={box.id}
                        className={[
                          'flex items-center gap-3 px-3 py-2 cursor-pointer text-sm',
                          i > 0 ? 'border-t border-surface-border' : '',
                          checked ? 'bg-brand-green/5' : 'bg-white',
                        ].join(' ')}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleBox(box.id)}
                          className="w-3.5 h-3.5 accent-brand-green flex-shrink-0"
                        />
                        <span className="flex-1 min-w-0">
                          <span className="font-medium text-ink-primary truncate block">{box.name}</span>
                          <span className="text-[10px] font-mono text-ink-faint">{box.id}</span>
                        </span>
                        <span className={[
                          'w-1.5 h-1.5 rounded-full flex-shrink-0',
                          box.status === 'online' ? 'bg-brand-green' : 'bg-ink-faint',
                        ].join(' ')} />
                      </label>
                    );
                  })}
                </div>
                {selectedBoxIds.length > 0 && (
                  <p className="text-xs text-brand-green font-medium">{selectedBoxIds.length} Box{selectedBoxIds.length !== 1 ? 'en' : ''} ausgewählt</p>
                )}
              </div>

              {/* Fehler / Erfolg */}
              {formError && (
                <p className="text-xs text-brand-red bg-brand-red-light border border-brand-red-mid rounded-lg px-3 py-2">{formError}</p>
              )}

              <button
                type="submit"
                disabled={saving}
                className={[
                  'w-full py-2.5 rounded-xl font-exo font-semibold text-sm transition-all duration-200 shadow-sm',
                  formSuccess
                    ? 'bg-brand-green text-white'
                    : 'bg-brand-green hover:bg-brand-green/90 text-white disabled:opacity-50 disabled:cursor-not-allowed',
                ].join(' ')}
              >
                {saving ? 'Wird gespeichert…' : formSuccess ? '✓ Eingerichtet' : 'Benachrichtigung einrichten'}
              </button>
            </form>
          </section>

          {/* ── Aktive Subscriptions ── */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-ink-muted border-b border-surface-border pb-2">
              Aktive Benachrichtigungen
            </h3>

            {loading && (
              <p className="text-xs text-ink-muted font-mono animate-pulse">Lade…</p>
            )}

            {!loading && subscriptions.length === 0 && (
              <p className="text-xs text-ink-faint">Noch keine Benachrichtigungen eingerichtet.</p>
            )}

            {subscriptions.map((sub) => {
              const boxNames = sub.box_ids.map(
                (id) => boxes.find((b) => b.id === id)?.name ?? id
              );
              return (
                <div
                  key={sub.id}
                  className="border border-surface-border rounded-xl p-3 bg-surface-subtle space-y-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      {sub.label && (
                        <p className="text-xs font-semibold text-ink-primary truncate">{sub.label}</p>
                      )}
                      <p className="font-mono text-xs text-ink-secondary break-all">{sub.email}</p>
                      <p className="text-[10px] text-ink-faint mt-0.5">
                        Seit {new Date(sub.created_at).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDelete(sub.id)}
                      className="flex-shrink-0 w-7 h-7 rounded-lg border border-surface-border bg-white flex items-center justify-center text-ink-muted hover:text-brand-red hover:border-brand-red-mid transition-colors"
                      title="Abmelden"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Box-Tags */}
                  <div className="flex flex-wrap gap-1">
                    {boxNames.map((name, i) => (
                      <span
                        key={i}
                        className="inline-block text-[10px] font-medium px-2 py-0.5 rounded-full bg-brand-green/10 text-brand-green border border-brand-green/20"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </section>
        </div>
      </div>
    </>
  );
}
